import { workoutOverrideSchema, workoutOverridesSchema, type WorkoutOverride } from '@/domain/health/workoutAppearance'
import { intakeSchema, savedFoodSchema, validateNutritionSnapshot, type IntakeRecord, type SavedFood } from '@/domain/health/nutrition'
import { moodRecordSchema, periodRecordSchema, validateJournalSnapshot, type MoodRecord, type PeriodRecord } from '@/domain/health/journal'
import type { Table } from 'dexie'

import type {
  DateRange,
  ManualBackupSnapshot,
  ManualHealthDataStore,
} from '@/domain/health/contracts'
import { parseIsoInstant } from '@/domain/health/time'
import type {
  AppSettings,
  DataSource,
  MetricRecord,
  MetricType,
  SleepRecord,
  WorkoutRecord,
} from '@/domain/health/types'

import {
  CareBabyDb,
  normalizeMetricRecord,
  normalizeSleepRecord,
  normalizeWorkoutRecord,
} from './CareBabyDb'

const DELETE_HEALTH_KIT_ERROR = '数据来源于 Apple 健康，不能在 CareBaby 中删除'
const OVERWRITE_HEALTH_KIT_ERROR = '已存在 Apple 健康记录，不能覆盖'

export function createDefaultSettings(): AppSettings {
  return { displayWeightUnit: 'kg', schemaVersion: 1 }
}

function clone<T>(value: T): T {
  return structuredClone(value)
}

function idDescending(left: string, right: string): number {
  if (left === right) return 0
  return left < right ? 1 : -1
}

function instantDescending<T>(field: (record: T) => string) {
  return (left: T & { id: string }, right: T & { id: string }): number => {
    const timeDifference = parseIsoInstant(field(right)).getTime() - parseIsoInstant(field(left)).getTime()
    return timeDifference || idDescending(left.id, right.id)
  }
}

const metricDescending = instantDescending<MetricRecord>((record) => record.recordedAt)
const sleepDescending = instantDescending<SleepRecord>((record) => record.endAt)
const workoutDescending = instantDescending<WorkoutRecord>((record) => record.startedAt)

function rangeMilliseconds(range: DateRange): { start: number; end: number; startIso: string; endIso: string } {
  const start = parseIsoInstant(range.startInclusive).getTime()
  const end = parseIsoInstant(range.endExclusive).getTime()
  return {
    start,
    end,
    startIso: new Date(start).toISOString(),
    endIso: new Date(end).toISOString(),
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0
}

function hasValidInstant(value: unknown): value is string {
  if (typeof value !== 'string') return false
  try {
    parseIsoInstant(value)
    return true
  } catch {
    return false
  }
}

function isManualMetric(value: unknown): value is MetricRecord {
  if (!isObject(value)) return false
  const type = value.type
  const validUnit =
    (type === 'weight' && value.unit === 'kg') ||
    (type === 'bodyFat' && value.unit === 'percent') ||
    (type === 'activeEnergy' && value.unit === 'kcal')
  return (
    isNonEmptyString(value.id) &&
    validUnit &&
    typeof value.value === 'number' &&
    Number.isFinite(value.value) &&
    hasValidInstant(value.recordedAt) &&
    isNonEmptyString(value.timeZone) &&
    isNonEmptyString(value.localDay) &&
    value.source === 'manual' &&
    hasValidInstant(value.createdAt) &&
    hasValidInstant(value.updatedAt)
  )
}

function isManualSleep(value: unknown): value is SleepRecord {
  return (
    isObject(value) &&
    isNonEmptyString(value.id) &&
    hasValidInstant(value.startAt) &&
    hasValidInstant(value.endAt) &&
    typeof value.durationMinutes === 'number' &&
    Number.isFinite(value.durationMinutes) &&
    isNonEmptyString(value.wakeTimeZone) &&
    isNonEmptyString(value.wakeLocalDay) &&
    value.source === 'manual' &&
    hasValidInstant(value.createdAt) &&
    hasValidInstant(value.updatedAt)
  )
}

function isManualWorkout(value: unknown): value is WorkoutRecord {
  return (
    isObject(value) &&
    isNonEmptyString(value.id) &&
    ['strength', 'running', 'walking', 'cycling', 'swimming', 'yoga', 'other'].includes(
      String(value.activityType),
    ) &&
    hasValidInstant(value.startedAt) &&
    isNonEmptyString(value.timeZone) &&
    isNonEmptyString(value.localDay) &&
    typeof value.durationMinutes === 'number' &&
    Number.isFinite(value.durationMinutes) &&
    (value.activeEnergyKcal === undefined ||
      (typeof value.activeEnergyKcal === 'number' && Number.isFinite(value.activeEnergyKcal))) &&
    (value.notes === undefined || typeof value.notes === 'string') &&
    (value.title === undefined || (typeof value.title === 'string' && value.title.trim().length > 0 && [...value.title].length <= 40)) &&
    (value.status === undefined || value.status === 'planned') &&
    value.source === 'manual' &&
    hasValidInstant(value.createdAt) &&
    hasValidInstant(value.updatedAt)
  )
}

function isSettings(value: unknown): value is AppSettings {
  return (
    isObject(value) &&
    (value.displayWeightUnit === 'kg' || value.displayWeightUnit === 'lb') &&
    value.schemaVersion === 1
  )
}

function assertValidSnapshot(snapshot: unknown): asserts snapshot is ManualBackupSnapshot {
  if (
    !isObject(snapshot) ||
    !Array.isArray(snapshot.metrics) ||
    !Array.isArray(snapshot.sleeps) ||
    !Array.isArray(snapshot.workouts) ||
    !isSettings(snapshot.settings) ||
    !snapshot.metrics.every(isManualMetric) ||
    !snapshot.sleeps.every(isManualSleep) ||
    !snapshot.workouts.every(isManualWorkout)
  ) {
    throw new Error('手动备份格式无效')
  }

  workoutOverridesSchema.parse(snapshot.workoutOverrides ?? [])
  validateJournalSnapshot(snapshot as unknown as ManualBackupSnapshot)
  validateNutritionSnapshot(snapshot as unknown as ManualBackupSnapshot)

  const dailyEnergy = new Set<string>()
  for (const record of snapshot.metrics) {
    if (record.type !== 'activeEnergy') continue
    if (dailyEnergy.has(record.localDay)) throw new Error('同一天只能有一条手动活动能量记录')
    dailyEnergy.add(record.localDay)
  }

  const wakeDays = new Set<string>()
  for (const record of snapshot.sleeps) {
    if (wakeDays.has(record.wakeLocalDay)) throw new Error('同一唤醒日只能有一条手动睡眠记录')
    wakeDays.add(record.wakeLocalDay)
  }
}

export class DexieManualHealthStore implements ManualHealthDataStore {
  readonly source = 'manual' as const

  async listWorkoutOverrides(): Promise<WorkoutOverride[]> {
    return (await this.db.workoutOverrides.toArray()).map(clone)
  }

  async putWorkoutOverride(record: WorkoutOverride): Promise<void> {
    await this.db.workoutOverrides.put(workoutOverrideSchema.parse(record))
  }

  async deleteWorkoutOverride(key: string): Promise<void> {
    await this.db.workoutOverrides.delete(key)
  }

  constructor(private readonly db: CareBabyDb = new CareBabyDb()) {}

  async listIntakes(): Promise<IntakeRecord[]> { return this.db.intakes.orderBy('localDay').reverse().toArray() }
  async putIntake(record: IntakeRecord): Promise<void> { await this.db.intakes.put(intakeSchema.parse(record)) }
  async putIntakes(records: IntakeRecord[]): Promise<void> {
    const parsed = records.map((record) => intakeSchema.parse(record))
    await this.db.transaction('rw', this.db.intakes, async () => { await this.db.intakes.bulkAdd(parsed) })
  }
  async deleteIntake(id: string): Promise<void> { await this.db.intakes.delete(id) }
  async listSavedFoods(): Promise<SavedFood[]> { return this.db.savedFoods.toArray() }
  async putSavedFood(record: SavedFood): Promise<void> {
    const parsed = savedFoodSchema.parse(record)
    await this.db.transaction('rw', this.db.savedFoods, async () => {
      const existing = await this.db.savedFoods.get(parsed.id)
      // Automatic caching cannot remove a favourite; explicit saves promote it.
      const libraryOnly = parsed.libraryOnly && (!existing || existing.libraryOnly) ? true : undefined
      const searchTerms = [...new Set([...(parsed.searchTerms ?? []), ...(existing?.searchTerms ?? [])])].slice(0, 20)
      await this.db.savedFoods.put({ ...parsed, createdAt: existing?.createdAt ?? parsed.createdAt, libraryOnly, searchTerms })
    })
  }
  async deleteSavedFood(id: string): Promise<void> {
    await this.db.transaction('rw', this.db.savedFoods, async () => {
      const existing = await this.db.savedFoods.get(id)
      if (existing?.food.origin === 'boohee') await this.db.savedFoods.put({ ...existing, libraryOnly: true })
      else await this.db.savedFoods.delete(id)
    })
  }

  async listMoods(): Promise<MoodRecord[]> {
    return this.db.moods.orderBy('localDay').reverse().toArray()
  }

  async listPeriods(): Promise<PeriodRecord[]> {
    return this.db.periods.orderBy('localDay').reverse().toArray()
  }

  async putMood(record: MoodRecord): Promise<void> {
    const parsed = moodRecordSchema.parse(record)
    await this.db.transaction('rw', this.db.moods, async () => {
      const existing = await this.db.moods.get(parsed.localDay)
      await this.db.moods.put({ ...parsed, createdAt: existing?.createdAt ?? parsed.createdAt })
    })
  }

  async deleteMood(localDay: string): Promise<void> {
    await this.db.moods.delete(localDay)
  }

  async updatePeriods(records: PeriodRecord[], removeDays: string[] = []): Promise<void> {
    const parsed = records.map((record) => periodRecordSchema.parse(record))
    await this.db.transaction('rw', this.db.periods, async () => {
      await this.db.periods.bulkDelete(removeDays)
      for (const record of parsed) {
        const existing = await this.db.periods.get(record.localDay)
        if (!existing) await this.db.periods.put(record)
      }
    })
  }

  async putMetric(record: MetricRecord): Promise<void> {
    if (record.source !== 'manual') throw new Error('只能写入手动记录')
    const normalized = normalizeMetricRecord(record)
    await this.db.transaction('rw', this.db.metrics, async () => {
      await this.assertNoImportedCollision(this.db.metrics, normalized.id)
      if (normalized.type === 'activeEnergy') {
        const duplicates = (await this.db.metrics.where('localDay').equals(normalized.localDay).toArray())
          .filter(
            (candidate) =>
              candidate.source === 'manual' &&
              candidate.type === 'activeEnergy' &&
              candidate.id !== normalized.id,
          )
          .map(({ id }) => id)
        await this.db.metrics.bulkDelete(duplicates)
      }
      await this.db.metrics.put(normalized)
    })
  }

  async putSleep(record: SleepRecord): Promise<void> {
    if (record.source !== 'manual') throw new Error('只能写入手动记录')
    const normalized = normalizeSleepRecord(record)
    await this.db.transaction('rw', this.db.sleeps, async () => {
      await this.assertNoImportedCollision(this.db.sleeps, normalized.id)
      const duplicate = (await this.db.sleeps.where('wakeLocalDay').equals(normalized.wakeLocalDay).toArray()).some(
        (candidate) => candidate.source === 'manual' && candidate.id !== normalized.id,
      )
      if (duplicate) throw new Error('同一唤醒日只能有一条手动睡眠记录')
      await this.db.sleeps.put(normalized)
    })
  }

  async putWorkout(record: WorkoutRecord): Promise<void> {
    if (record.source !== 'manual') throw new Error('只能写入手动记录')
    const normalized = normalizeWorkoutRecord(record)
    await this.db.transaction('rw', this.db.workouts, async () => {
      await this.assertNoImportedCollision(this.db.workouts, normalized.id)
      await this.db.workouts.put(normalized)
    })
  }

  async listMetrics(range: DateRange): Promise<MetricRecord[]> {
    const bounds = rangeMilliseconds(range)
    const records = await this.db.metrics
      .where('recordedAt')
      .between(bounds.startIso, bounds.endIso, true, false)
      .toArray()
    return records
      .filter((record) => {
        const timestamp = parseIsoInstant(record.recordedAt).getTime()
        return record.source === 'manual' && timestamp >= bounds.start && timestamp < bounds.end
      })
      .sort(metricDescending)
      .map(clone)
  }

  async latestMetric(type: MetricType, beforeExclusive: string): Promise<MetricRecord | null> {
    const boundary = parseIsoInstant(beforeExclusive).getTime()
    const records = await this.db.metrics.where('type').equals(type).toArray()
    const latest = records
      .filter(
        (record) =>
          record.source === 'manual' && parseIsoInstant(record.recordedAt).getTime() < boundary,
      )
      .sort(metricDescending)[0]
    return latest === undefined ? null : clone(latest)
  }

  async listSleeps(range: DateRange): Promise<SleepRecord[]> {
    const bounds = rangeMilliseconds(range)
    const records = await this.db.sleeps
      .where('endAt')
      .between(bounds.startIso, bounds.endIso, true, false)
      .toArray()
    return records
      .filter((record) => {
        const timestamp = parseIsoInstant(record.endAt).getTime()
        return record.source === 'manual' && timestamp >= bounds.start && timestamp < bounds.end
      })
      .sort(sleepDescending)
      .map(clone)
  }

  async listWorkouts(range: DateRange): Promise<WorkoutRecord[]> {
    const bounds = rangeMilliseconds(range)
    const records = await this.db.workouts
      .where('startedAt')
      .between(bounds.startIso, bounds.endIso, true, false)
      .toArray()
    return records
      .filter((record) => {
        const timestamp = parseIsoInstant(record.startedAt).getTime()
        return record.source === 'manual' && timestamp >= bounds.start && timestamp < bounds.end
      })
      .sort(workoutDescending)
      .map(clone)
  }

  async listMetricHistory(type: MetricType): Promise<MetricRecord[]> {
    return (await this.db.metrics.where('type').equals(type).toArray())
      .filter((record) => record.source === 'manual')
      .sort(metricDescending)
      .map(clone)
  }

  async listSleepHistory(): Promise<SleepRecord[]> {
    return (await this.db.sleeps.where('source').equals('manual').toArray())
      .sort(sleepDescending)
      .map(clone)
  }

  async listWorkoutHistory(): Promise<WorkoutRecord[]> {
    return (await this.db.workouts.where('source').equals('manual').toArray())
      .sort(workoutDescending)
      .map(clone)
  }

  async getManualMetric(id: string): Promise<MetricRecord | null> {
    return this.getManual(this.db.metrics, id)
  }

  async getManualSleep(id: string): Promise<SleepRecord | null> {
    return this.getManual(this.db.sleeps, id)
  }

  async getManualWorkout(id: string): Promise<WorkoutRecord | null> {
    return this.getManual(this.db.workouts, id)
  }

  async getSettings(): Promise<AppSettings> {
    const row = await this.db.settings.get('app')
    return row === undefined ? createDefaultSettings() : clone(row.value)
  }

  async putSettings(settings: AppSettings): Promise<void> {
    if (!isSettings(settings)) throw new Error('设置格式无效')
    await this.db.transaction('rw', this.db.settings, async () => {
      await this.db.settings.put({ key: 'app', value: clone(settings) })
    })
  }

  async deleteManual(kind: 'metric' | 'sleep' | 'workout', id: string): Promise<void> {
    const table = this.tableFor(kind)
    await this.db.transaction('rw', table, async () => {
      const record = await table.get(id)
      if (record === undefined) return
      if (record.source !== 'manual') throw new Error(DELETE_HEALTH_KIT_ERROR)
      await table.delete(id)
    })
  }

  async readManualSnapshot(): Promise<ManualBackupSnapshot> {
    return this.db.transaction('r', [this.db.metrics, this.db.sleeps, this.db.workouts, this.db.settings, this.db.moods, this.db.periods, this.db.intakes, this.db.savedFoods, this.db.workoutOverrides], async () => {
      const [metrics, sleeps, workouts, settings, moods, periods, intakes, savedFoods, workoutOverrides] = await Promise.all([
        this.db.metrics.where('source').equals('manual').toArray(),
        this.db.sleeps.where('source').equals('manual').toArray(),
        this.db.workouts.where('source').equals('manual').toArray(),
        this.getSettings(),
        this.listMoods(),
        this.listPeriods(),
        this.listIntakes(),
        this.listSavedFoods(),
        this.listWorkoutOverrides(),
      ])
      return {
        metrics: metrics.sort(metricDescending).map(clone),
        sleeps: sleeps.sort(sleepDescending).map(clone),
        workouts: workouts.sort(workoutDescending).map(clone),
        settings: clone(settings),
        moods,
        periods,
        intakes,
        savedFoods,
        workoutOverrides,
      }
    })
  }

  async replaceManualSnapshot(snapshot: ManualBackupSnapshot): Promise<void> {
    assertValidSnapshot(snapshot)
    const replacement: ManualBackupSnapshot = {
      metrics: snapshot.metrics.map(normalizeMetricRecord),
      sleeps: snapshot.sleeps.map(normalizeSleepRecord),
      workouts: snapshot.workouts.map(normalizeWorkoutRecord),
      settings: clone(snapshot.settings),
      moods: clone(snapshot.moods ?? []),
      periods: clone(snapshot.periods ?? []),
      intakes: clone(snapshot.intakes ?? []),
      savedFoods: clone(snapshot.savedFoods ?? []),
      workoutOverrides: clone(snapshot.workoutOverrides ?? []),
    }
    await this.db.transaction(
      'rw',
      [this.db.metrics, this.db.sleeps, this.db.workouts, this.db.settings, this.db.moods, this.db.periods, this.db.intakes, this.db.savedFoods, this.db.workoutOverrides],
      async () => {
        await this.assertNoImportedCollisions(
          this.db.metrics,
          replacement.metrics.map(({ id }) => id),
        )
        await this.assertNoImportedCollisions(
          this.db.sleeps,
          replacement.sleeps.map(({ id }) => id),
        )
        await this.assertNoImportedCollisions(
          this.db.workouts,
          replacement.workouts.map(({ id }) => id),
        )
        await this.deleteSource(this.db.metrics, 'manual')
        await this.deleteSource(this.db.sleeps, 'manual')
        await this.deleteSource(this.db.workouts, 'manual')
        await this.db.workoutOverrides.clear()
        await this.db.intakes.clear()
        await this.db.savedFoods.clear()
        await this.db.moods.clear()
        await this.db.periods.clear()
        await this.db.moods.bulkPut(replacement.moods ?? [])
        await this.db.periods.bulkPut(replacement.periods ?? [])
        await this.db.intakes.bulkPut(replacement.intakes ?? [])
        await this.db.savedFoods.bulkPut(replacement.savedFoods ?? [])
        await this.db.workoutOverrides.bulkPut(replacement.workoutOverrides ?? [])
        await this.db.metrics.bulkPut(replacement.metrics)
        await this.db.sleeps.bulkPut(replacement.sleeps)
        await this.db.workouts.bulkPut(replacement.workouts)
        await this.db.settings.put({ key: 'app', value: replacement.settings })
      },
    )
  }

  async clearManualData(): Promise<void> {
    await this.db.transaction(
      'rw',
      [this.db.metrics, this.db.sleeps, this.db.workouts, this.db.settings, this.db.moods, this.db.periods, this.db.intakes, this.db.savedFoods, this.db.workoutOverrides],
      async () => {
        await this.db.workoutOverrides.clear()
        await this.db.intakes.clear()
        await this.db.savedFoods.clear()
        await this.db.moods.clear()
        await this.db.periods.clear()
        await this.db.metrics.clear()
        await this.db.sleeps.clear()
        await this.db.workouts.clear()
        await this.db.settings.put({ key: 'app', value: createDefaultSettings() })
      },
    )
  }

  private async getManual<T extends { source: DataSource }>(
    table: Table<T, string>,
    id: string,
  ): Promise<T | null> {
    const record = await table.get(id)
    return record === undefined || record.source !== 'manual' ? null : clone(record)
  }

  private tableFor(
    kind: 'metric' | 'sleep' | 'workout',
  ): Table<MetricRecord | SleepRecord | WorkoutRecord, string> {
    if (kind === 'metric') return this.db.metrics
    if (kind === 'sleep') return this.db.sleeps
    return this.db.workouts
  }

  private async deleteSource<T extends { id: string; source: DataSource }>(
    table: Table<T, string>,
    source: T['source'],
  ): Promise<void> {
    const ids = (await table.where('source').equals(source).primaryKeys()) as string[]
    await table.bulkDelete(ids)
  }

  private async assertNoImportedCollision<T extends { source: DataSource }>(
    table: Table<T, string>,
    id: string,
  ): Promise<void> {
    const existing = await table.get(id)
    if (existing !== undefined && existing.source !== 'manual') throw new Error(OVERWRITE_HEALTH_KIT_ERROR)
  }

  private async assertNoImportedCollisions<T extends { source: DataSource }>(
    table: Table<T, string>,
    ids: string[],
  ): Promise<void> {
    const existing = await table.bulkGet(ids)
    if (existing.some((record) => record !== undefined && record.source !== 'manual')) {
      throw new Error(OVERWRITE_HEALTH_KIT_ERROR)
    }
  }
}
